import { AlignmentTransform } from "@/modes/alignment-modes-commons/alignment-transform";
import { TwoPointsPairsAlignmentAnchorPositions } from "@/modes/alignment-modes-commons/compute-split-screen-alignment";
import {
  AlignmentViewLayout,
  SplitDirection,
} from "@/store/modes/alignment-ui-types";
import { GUID } from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Vector3Tuple } from "three";

/** State managing data exchange during sheet-to-sheet alignment session */
type SheetToSheetAlignmentToolState = {
  /**
   * Aligned sheet ID used during current alignment session
   * It's defined only within life cycle of sheet-to-sheet alignment workflow
   * At the end of each alignment cycle it must be reset to undefined to prevent reusing it in next session of alignment
   */
  alignedSheetId?: GUID;

  /**
   * Sheet ID used as reference sheet during current alignment session.
   * It's defined only within life cycle of sheet-to-sheet alignment workflow
   * At the end of each alignment cycle it must be reset to undefined to prevent reusing it in next session of alignment
   */
  referenceSheetId?: GUID;

  /**
   * Incremental transform of the between sheets. Translations are in meters.
   * At the end of each alignment cycle it must be reset to undefined to prevent reusing it in next session of alignment
   */
  incrementalTransform?: AlignmentTransform;

  /** anchor positions defined in split screen alignment */
  alignmentAnchorPositions: TwoPointsPairsAlignmentAnchorPositions;

  /** sheet to sheet alignment screen layout */
  alignmentLayout: AlignmentViewLayout;

  /** the alignment screen split direction */
  splitDirection: SplitDirection;
};

const initialState: SheetToSheetAlignmentToolState = {
  alignmentAnchorPositions: {},
  alignmentLayout: AlignmentViewLayout.splitScreen,
  splitDirection: SplitDirection.horizontalSplit,
};

const sheetToSheetAlignmentModeSlice = createSlice({
  initialState,
  name: "sheetToSheetAlignmentMode",
  reducers: {
    setAlignedSheetId(state, action: PayloadAction<GUID>) {
      state.alignedSheetId = action.payload;
    },

    setReferenceSheetId(state, action: PayloadAction<GUID>) {
      state.referenceSheetId = action.payload;
    },

    setSheetToSheetIncrementalTransform(
      state,
      action: PayloadAction<AlignmentTransform | undefined>,
    ) {
      state.incrementalTransform = action.payload;
    },

    setMovingElementAnchor1ForSheetToSheetAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.movingElementAnchor1 = action.payload;
    },

    setMovingElementAnchor2ForSheetToSheetAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.movingElementAnchor2 = action.payload;
    },

    setReferenceElementAnchor1ForSheetToSheetAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.referenceElementAnchor1 = action.payload;
    },

    setReferenceElementAnchor2ForSheetToSheetAlignment(
      state,
      action: PayloadAction<Vector3Tuple | undefined>,
    ) {
      state.alignmentAnchorPositions.referenceElementAnchor2 = action.payload;
    },

    resetSheetToSheetAlignment(state) {
      state.incrementalTransform = undefined;
      state.alignmentAnchorPositions = {};
      state.alignmentLayout = AlignmentViewLayout.splitScreen;
      state.splitDirection = SplitDirection.horizontalSplit;
    },

    setSheetToSheetAlignmentLayout(
      state,
      action: PayloadAction<AlignmentViewLayout>,
    ) {
      state.alignmentLayout = action.payload;
    },

    setSheetToSheetAlignmentSplitDirection(
      state,
      action: PayloadAction<SplitDirection>,
    ) {
      state.splitDirection = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const {
  setAlignedSheetId,
  setReferenceSheetId,
  resetSheetToSheetAlignment,
  setSheetToSheetIncrementalTransform,
  setMovingElementAnchor1ForSheetToSheetAlignment,
  setMovingElementAnchor2ForSheetToSheetAlignment,
  setReferenceElementAnchor1ForSheetToSheetAlignment,
  setReferenceElementAnchor2ForSheetToSheetAlignment,
  setSheetToSheetAlignmentSplitDirection,
  setSheetToSheetAlignmentLayout,
} = sheetToSheetAlignmentModeSlice.actions;

export const sheetToSheetAlignmentModeReducer =
  sheetToSheetAlignmentModeSlice.reducer;
